/**
 * Queries that wait on a background job, refreshed by `/sync` events instead of
 * a poll ("Events, never polling", docs/architecture/code-patterns.md).
 *
 * Each of these used to re-read on a 2-5 s `refetchInterval` while its job ran.
 * Every write those jobs make already reaches `/sync`: the derivation and
 * video-insight state writes are an artifact CAS (`updateMany`, broadcast by
 * the audit extension's bulk hook), and the ORCID import's status write on the
 * workspace row broadcasts a `workspace` update. So the handler only has to
 * name the right keys:
 *
 * - on a change, `invalidateJobStateQueries` (called from the batched
 *   invalidation in `queryClient.ts`);
 * - on every `/sync` room join, `invalidateQueriesAwaitingJobs`, for the write
 *   that landed while the socket was down, or before the first join (a page
 *   that read `running` a moment before the room was joined would otherwise
 *   wait for an event it already missed).
 *
 * A Live protocol run's reads are refreshed here too: on a `protocol_run`
 * change the run's page, the lists and counts it is in, and on a join every
 * run read still showing an open run (section below).
 *
 * Which queries each path touches:
 * - a change: derivation state and video insights when it names their
 *   artifact; the import chips on any artifact change; the onboarding sources
 *   while they show work in flight.
 * - a join: the import chips always (their cached list cannot know of an
 *   import started elsewhere); the others while they show work in flight. A
 *   job STARTED elsewhere on a screen that looked settled while the socket
 *   was down waits for its next event, as it did under the old polls, which
 *   never re-read a settled state either.
 * Wherever a rule reads cached data, a query still on its FIRST fetch counts
 * as in flight: it has nothing to judge yet, and the write it waits for may
 * already have broadcast. Only mounted queries refetch. Kept out of
 * `queryClient.ts` so the hooks can import the keys without importing the
 * client.
 */
import { isOpenRunStatus } from '@bower/shared/types/protocol-runs';
import type { ProtocolRunStatus } from '@bower/shared';
import type { Query, QueryClient } from '@tanstack/react-query';

/** A protocol's derivation state blob (`useDerivationState`). */
export const DERIVATION_STATE_KEY = ['protocol-derivation-state'] as const;
export const derivationStateKey = (protocolId: string | undefined) =>
  [...DERIVATION_STATE_KEY, protocolId] as const;

/** One video's insight proposals (`useVideoInsights`). */
export const VIDEO_INSIGHTS_KEY = ['video-insights'] as const;
export const videoInsightsKey = (videoArtifactId: string) =>
  [...VIDEO_INSIGHTS_KEY, videoArtifactId] as const;

/** The onboarding sources and ORCID import (`useOnboardingAhaContext`). */
export const ONBOARDING_AHA_CONTEXT_KEY = ['onboarding-aha-context'] as const;
export const onboardingAhaContextKey = (
  workspaceId: string | null,
  artifactIds: readonly string[],
) => [...ONBOARDING_AHA_CONTEXT_KEY, workspaceId, [...artifactIds].sort()] as const;

/** Root of `protocolImportsKey` (queryClient.ts), which imports this module. */
const PROTOCOL_IMPORTS_ROOT = ['protocol-imports'] as const;

const inFlight = (status: unknown): boolean => status === 'queued' || status === 'running';

function derivationInFlight(data: unknown): boolean {
  return inFlight((data as { status?: unknown } | null | undefined)?.status);
}

function videoInsightQueued(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  return Object.values(data as Record<string, { status?: unknown } | undefined>).some(
    (state) => state?.status === 'queued',
  );
}

function onboardingAwaiting(data: unknown): boolean {
  const ctx = data as
    | { sources?: ReadonlyArray<{ readiness?: unknown }>; orcidImport?: { state?: unknown } }
    | undefined;
  return (
    (Array.isArray(ctx?.sources) && ctx.sources.some((s) => s?.readiness === 'processing')) ||
    ctx?.orcidImport?.state === 'queued'
  );
}

/**
 * Whether a cached query may be waiting on a write: its data says so, or it is
 * still fetching and has none to judge. Invalidating a fetching query cancels
 * that read and starts a new one, which reads the row after the write.
 */
function awaiting(query: Query, showsWorkInFlight: (data: unknown) => boolean): boolean {
  return query.state.fetchStatus === 'fetching' || showsWorkInFlight(query.state.data);
}

/** The slice of one batched `/sync` change this module reads. */
export interface JobStateChange {
  readonly entityType: string;
  readonly changeType: 'created' | 'updated' | 'deleted';
  readonly entityId: string;
  /** A `protocol_run` change's protocol; absent on a heartbeat. */
  readonly data?: { readonly protocolId?: string };
}

// ─── Live protocol runs ──────────────────────────────────────────────────────
//
// Every run write broadcasts `protocol_run` after commit (backend
// `broadcastRunChange`), so the run reads (`hooks/data/useProtocolRuns.ts`)
// refresh here and never poll.

/** The `/sync` entity type of a run write (backend `broadcastRunChange`). */
export const PROTOCOL_RUN_ENTITY_TYPE = 'protocol_run';

/** The run reads' key roots (`protocolRunKey`, `protocolRunsKey`, ... in `queryClient.ts`). */
const RUN_ROOTS: ReadonlySet<unknown> = new Set([
  'protocol-run',
  'protocol-runs',
  'protocol-run-counts',
  'protocol-run-version-counts',
]);

/** The run keys' second segment (`protocolRunKey`, `protocolRunsKey`, ...). */
const runKeyArgs = (query: Query) =>
  (query.queryKey[1] ?? {}) as {
    readonly runId?: string;
    readonly protocolId?: string;
    readonly ids?: readonly string[];
  };

const isOpenRun = (status: unknown): boolean =>
  typeof status === 'string' && isOpenRunStatus(status as ProtocolRunStatus);

type RunPages = {
  pages?: ReadonlyArray<{ items?: ReadonlyArray<{ id?: unknown; status?: unknown }> }>;
};
const runRows = (data: unknown) =>
  ((data as RunPages | undefined)?.pages ?? []).flatMap((p) => p.items ?? []);

function countsShowOpenRun(data: unknown): boolean {
  if (!(data instanceof Map)) return false;
  return [...data.values()].some((count: { openCount?: number }) => (count.openCount ?? 0) > 0);
}

/** Does this run change move what the query shows? A heartbeat names no
 * protocol and moves only `lastActiveAt`, so it reaches the lists holding the
 * run and the counts showing an open run (the grid's Last run cell shows an
 * open run's `lastActivityAt` to the minute). */
function runChangeReaches(query: Query, change: JobStateChange): boolean {
  const key = runKeyArgs(query);
  const protocolId = change.data?.protocolId;
  switch (query.queryKey[0]) {
    case 'protocol-run':
      return key.runId === change.entityId;
    case 'protocol-runs':
      return protocolId
        ? key.protocolId === protocolId
        : runRows(query.state.data).some((row) => row.id === change.entityId);
    case 'protocol-run-counts':
      return protocolId
        ? (key.ids ?? []).includes(protocolId)
        : countsShowOpenRun(query.state.data);
    case 'protocol-run-version-counts':
      return change.changeType === 'created' && key.protocolId === protocolId;
    default:
      return false;
  }
}

/** Refresh the run reads a batch of `protocol_run` changes moved. */
function invalidateRunQueries(client: QueryClient, changes: readonly JobStateChange[]): void {
  const runChanges = changes.filter((c) => c.entityType === PROTOCOL_RUN_ENTITY_TYPE);
  if (runChanges.length === 0) return;
  void client.invalidateQueries({
    predicate: (query) => runChanges.some((change) => runChangeReaches(query, change)),
  });
}

/** A run read still showing a run that can move (or on its first fetch). */
function runAwaiting(query: Query): boolean {
  const data = query.state.data;
  switch (query.queryKey[0]) {
    case 'protocol-run':
      return isOpenRun((data as { status?: unknown } | undefined)?.status);
    case 'protocol-runs':
      return runRows(data).some((row) => isOpenRun(row.status));
    case 'protocol-run-counts':
      return countsShowOpenRun(data);
    default:
      return false;
  }
}

/** A run change this screen received: when, and the server's send time. */
type RunPush = { readonly receivedAt: number; readonly sentAt: string };

/** Run changes this screen has received but not yet rendered, for the `seen` clock point. */
const pendingRunPushes = new Map<string, RunPush>();

/**
 * Note a `protocol_run` event's arrival, when a run page for it is
 * open, so the page can report how long the change took to show
 * (`takeRunPush`). The newest event wins: the page renders the newest state.
 */
export function noteRunPush(
  client: QueryClient,
  change: { readonly entityType: string; readonly entityId: string; readonly timestamp: string },
): void {
  if (change.entityType !== PROTOCOL_RUN_ENTITY_TYPE) return;
  const open = client
    .getQueryCache()
    .findAll({ queryKey: ['protocol-run'], type: 'active' })
    .some((query) => runKeyArgs(query).runId === change.entityId);
  if (open) {
    pendingRunPushes.set(change.entityId, { receivedAt: Date.now(), sentAt: change.timestamp });
  }
}

/**
 * The run change a page has just rendered, once: only when its data was read
 * after the event arrived, so a render of older data does not claim it.
 */
export function takeRunPush(runId: string, dataUpdatedAt: number): RunPush | null {
  const pending = pendingRunPushes.get(runId);
  if (!pending || dataUpdatedAt < pending.receivedAt) return null;
  pendingRunPushes.delete(runId);
  return pending;
}

/** Forget a change a page never rendered, when the page goes: a later visit
 * must not report the time away as latency. */
export function dropRunPush(runId: string): void {
  pendingRunPushes.delete(runId);
}

/**
 * Refresh what a batch of `/sync` changes may have moved.
 *
 * - Derivation state and video insights live ON the artifact the event names,
 *   so exactly that id's query.
 * - The library's import chips on any artifact change: the derivation write
 *   names a protocol the cached list may not hold yet (an import or derive
 *   started in another tab or by the agent).
 * - The onboarding sources while one is still processing or the ORCID import
 *   is queued: readiness is the import's own extraction, and the ORCID papers
 *   arrive as new artifacts plus a workspace update.
 */
export function invalidateJobStateQueries(
  client: QueryClient,
  changes: readonly JobStateChange[],
  isArtifactType: (entityType: string) => boolean,
): void {
  invalidateRunQueries(client, changes);
  const artifactChanges = changes.filter((c) => isArtifactType(c.entityType));
  const touchesWorkspace = changes.some((c) => c.entityType === 'workspace');
  if (artifactChanges.length === 0 && !touchesWorkspace) return;

  for (const { entityId } of artifactChanges) {
    void client.invalidateQueries({ queryKey: derivationStateKey(entityId) });
    void client.invalidateQueries({ queryKey: videoInsightsKey(entityId) });
  }
  // Any artifact change, not only while one runs: an import or derive started
  // in another tab or by the agent shows up first as a create, or as a state
  // write on an existing protocol, and neither is in the cached list yet. One
  // capped workspace query, refetched only while the library is mounted.
  if (artifactChanges.length > 0) {
    void client.invalidateQueries({ queryKey: PROTOCOL_IMPORTS_ROOT });
  }
  void client.invalidateQueries({
    queryKey: ONBOARDING_AHA_CONTEXT_KEY,
    predicate: (query) => awaiting(query, onboardingAwaiting),
  });
}

/**
 * Room-join catch-up: refetch the cached job queries an event may have passed
 * by while this client was not in the room (the rules are in the module
 * header). Event-driven (the join), never on a timer. Returns how many queries
 * it invalidated.
 */
export function invalidateQueriesAwaitingJobs(client: QueryClient): number {
  const checks: ReadonlyArray<readonly [string, (data: unknown) => boolean]> = [
    [DERIVATION_STATE_KEY[0], derivationInFlight],
    [VIDEO_INSIGHTS_KEY[0], videoInsightQueued],
    // Always, matching the change path: see the module header.
    [PROTOCOL_IMPORTS_ROOT[0], () => true],
    [ONBOARDING_AHA_CONTEXT_KEY[0], onboardingAwaiting],
  ];
  let count = 0;
  for (const query of client.getQueryCache().getAll()) {
    // A run read: a run write that landed while this client was out of the room.
    const showsWork = RUN_ROOTS.has(query.queryKey[0])
      ? () => runAwaiting(query)
      : checks.find(([root]) => query.queryKey[0] === root)?.[1];
    if (!showsWork || !awaiting(query, showsWork)) continue;
    void client.invalidateQueries({ queryKey: query.queryKey, exact: true });
    count += 1;
  }
  return count;
}
